/* ==========================================================================
   editorial-grid.js — 2c

   Two jobs, and nothing else. Every visual state in all three presets is pure
   CSS: the hover zoom is .kit-zoom-host:hover .kit-zoom, and the greyscale is
   .kit-media-desaturate. With this file blocked the section still renders and
   still responds to a mouse.

   1. THE TOUCH FALLBACK FOR GREYSCALE. This is the reason the file exists.
      The design shows photographs desaturated and returns colour on :hover.
      :hover never fires on a touch screen, so on the majority of a salon's
      traffic a naive build shows nine or ten permanently black-and-white
      images — not a subtle degradation, a different design. An
      IntersectionObserver adds .is-in-view to each media layer as it scrolls
      into frame, which is the release hook kit-primitives.css already ships
      for .kit-media-desaturate.

      It runs when the merchant asked for it (colour_on = in_view) AND
      automatically whenever the device cannot hover, even if the merchant left
      colour_on = hover. Choosing "hover" is a choice about pointers, not a
      choice to ship a broken gallery to phones.

      Every failure path releases colour rather than withholding it: no
      IntersectionObserver, a thrown constructor, an empty grid — all end with
      the images in colour. Grey is never the fallback state.

   2. PAUSING OFF-SCREEN VIDEO. snippets/kit-media-cover.liquid emits
      data-kit-pause-offscreen as a HOOK and documents that the consuming
      section drives it (there is no shared media JS in the kit). Optional
      video tiles in a twelve-tile grid would otherwise all decode at once.

   API surface used from the kit: none. This section needs no tabs, no scroll
   lock, no category bus, and window.KitReveal already owns reveal-on-scroll.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-editorial-grid]';
  var MEDIA_SELECTOR = '.kit-media-desaturate:not(.kit-media-desaturate--always)';
  var VIDEO_SELECTOR = '[data-kit-pause-offscreen]';
  var IN_VIEW_CLASS = 'is-in-view';

  /* A fine pointer that can hover. Anything else — touch, pen, a TV remote, a
     Windows tablet in tablet mode — gets the observer. */
  var hoverQuery = null;
  if (typeof window.matchMedia === 'function') {
    hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  }

  function canHover() {
    return hoverQuery ? hoverQuery.matches : true;
  }

  function supportsObserver() {
    return typeof window.IntersectionObserver === 'function';
  }

  /* Keyed by the root element, so a section re-rendered by the theme editor
     replaces its own controller and cannot leak the previous one's observers. */
  var controllers = new WeakMap();

  function EditorialGrid(root) {
    this.root = root;
    this.colourObserver = null;
    this.videoObserver = null;
    this.onHoverChange = this.refreshColourMode.bind(this);

    if (hoverQuery) {
      if (typeof hoverQuery.addEventListener === 'function') {
        hoverQuery.addEventListener('change', this.onHoverChange);
      } else if (typeof hoverQuery.addListener === 'function') {
        /* Safari < 14 */
        hoverQuery.addListener(this.onHoverChange);
      }
    }

    this.refreshColourMode();
    this.initVideo();
  }

  EditorialGrid.prototype.media = function () {
    return Array.prototype.slice.call(this.root.querySelectorAll(MEDIA_SELECTOR));
  };

  /* colour_on: 'always' is handled entirely in Liquid — the snippet emits
     .kit-media-desaturate--always and there is nothing to observe. */
  EditorialGrid.prototype.wantsInView = function () {
    var mode = (this.root.getAttribute('data-colour-on') || 'hover').replace(/-/g, '_');
    if (mode === 'always') return false;
    if (mode === 'in_view') return true;
    return !canHover();
  };

  EditorialGrid.prototype.release = function (element) {
    if (element) element.classList.add(IN_VIEW_CLASS);
  };

  EditorialGrid.prototype.releaseAll = function () {
    this.media().forEach(this.release, this);
  };

  EditorialGrid.prototype.refreshColourMode = function () {
    if (!this.wantsInView()) {
      /* A pointer arrived on a device that had none. Hand the tiles back to
         :hover by clearing the class the observer added; leaving it on would
         freeze every tile in colour and lose the effect entirely. */
      this.teardownColour();
      this.media().forEach(function (element) {
        element.classList.remove(IN_VIEW_CLASS);
      });
      return;
    }

    if (this.colourObserver) return;

    var elements = this.media();
    if (!elements.length) return;

    if (!supportsObserver()) {
      this.releaseAll();
      return;
    }

    var self = this;
    try {
      this.colourObserver = new window.IntersectionObserver(
        function (entries, observer) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            self.release(entry.target);
            /* One-way. A tile that has been seen stays in colour — re-greying
               it on scroll-out reads as a rendering fault, not an effect. */
            observer.unobserve(entry.target);
          });
        },
        { threshold: 0.35 }
      );
    } catch (error) {
      this.releaseAll();
      return;
    }

    elements.forEach(function (element) {
      self.colourObserver.observe(element);
    });
  };

  /* Playback follows visibility. play() rejects on a paused-media policy in
     several engines, so the promise is caught — an unhandled rejection here
     would surface in the merchant's console on every scroll. */
  EditorialGrid.prototype.initVideo = function () {
    var videos = Array.prototype.slice.call(this.root.querySelectorAll(VIDEO_SELECTOR));
    if (!videos.length || !supportsObserver()) return;

    this.videoObserver = new window.IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (typeof video.play !== 'function') return;
          if (entry.isIntersecting) {
            if (video.autoplay && video.paused) {
              var played = video.play();
              if (played && typeof played.catch === 'function') played.catch(function () {});
            }
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.05 }
    );

    var observer = this.videoObserver;
    videos.forEach(function (video) {
      observer.observe(video);
    });
  };

  EditorialGrid.prototype.teardownColour = function () {
    if (this.colourObserver) {
      this.colourObserver.disconnect();
      this.colourObserver = null;
    }
  };

  /* Called on shopify:block:select. A block added in the theme editor may sit
     outside the editor's viewport, so it would never intersect and would stay
     grey — which the merchant reads as "the image I just uploaded is broken".
     Rescan for it, then release the selected tile unconditionally. */
  EditorialGrid.prototype.rescan = function (target) {
    if (this.colourObserver && this.wantsInView()) {
      var observer = this.colourObserver;
      this.media().forEach(function (element) {
        if (!element.classList.contains(IN_VIEW_CLASS)) observer.observe(element);
      });
    }
    if (target) {
      Array.prototype.slice.call(target.querySelectorAll(MEDIA_SELECTOR)).forEach(this.release, this);
    }
    this.initVideo();
  };

  EditorialGrid.prototype.destroy = function () {
    this.teardownColour();
    if (this.videoObserver) {
      this.videoObserver.disconnect();
      this.videoObserver = null;
    }
    if (hoverQuery) {
      if (typeof hoverQuery.removeEventListener === 'function') {
        hoverQuery.removeEventListener('change', this.onHoverChange);
      } else if (typeof hoverQuery.removeListener === 'function') {
        hoverQuery.removeListener(this.onHoverChange);
      }
    }
  };

  function mount(root) {
    if (!root) return null;
    var existing = controllers.get(root);
    if (existing) existing.destroy();
    var controller = new EditorialGrid(root);
    controllers.set(root, controller);
    return controller;
  }

  function mountAll(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.slice.call(context.querySelectorAll(ROOT_SELECTOR)).forEach(mount);
    if (context !== document && context.matches && context.matches(ROOT_SELECTOR)) mount(context);
  }

  function rootIn(target) {
    if (!target || !target.querySelector) return null;
    return target.matches && target.matches(ROOT_SELECTOR) ? target : target.querySelector(ROOT_SELECTOR);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      mountAll(document);
    });
  } else {
    mountAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    mountAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = rootIn(event.target);
    if (!root) return;
    var controller = controllers.get(root);
    if (controller) {
      controller.destroy();
      controllers.delete(root);
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest ? event.target.closest(ROOT_SELECTOR) : null;
    if (!root) return;
    var controller = controllers.get(root) || mount(root);
    if (controller) controller.rescan(event.target);
  });
})();
